<template>
  <div class="h-full w-full bg-white shadow-lg rounded-lg p-2">
    <div
      v-if="isLoading"
      class="grid grid-cols-1 sm:grid-cols-2 lg:flex gap-4 px-1 pt-4"
    >
      <SkeletonCard v-for="index in 4" :key="index" />
    </div>

    <div
      v-else
      class="mb-6 xxl:mb-4 grid grid-cols-1 sm:grid-cols-2 lg:flex gap-4 px-1 pt-4"
    >
      <GeneralCard
        title="Propostas Realizadas"
        :value="registerCardsData.totalProposals || 0"
      />
      <GeneralCard
        title="Aguardando Aprovação"
        :value="registerCardsData.totalAwaitingApprovalProposals || 0"
      />
      <GeneralCard
        title="Propostas Finalizadas"
        :value="registerCardsData.totalApprovalProposals || 0"
      />
      <GeneralCard
        title="Propostas Pendenciadas"
        :value="registerCardsData.totalPendingProposals || 0"
      />
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 h-full mt-8">
      <div class="p-2 flex flex-col gap-4">
        <div class="p-2 mt-1 shadow-md flex-grow">
          <GenericBarChart
            :data="approvedProposalsByWeek"
            title="Aprovações Semanais"
            :isLoading="isLoading"
          />
        </div>
        <div class="p-2 mt-1 shadow-md flex-grow">
          <GenericPieChart
            :value="conversionRateData"
            :title="'Conversão de Propostas Finalizadas'"
            :isLoading="isLoading"
            :legend="`* ${conversionRateData.toFixed(
              2,
            )}% das propostas aguardando aprovação foram finalizadas.`"
          />
        </div>
      </div>
      <div class="p-2 flex-grow">
        <GenericTable
          :headers="tableHeaders"
          :data="registerDetailsStatus"
          :is-loading="isLoading"
          :table-style="tableStyle"
          :thead-style="theadStyle"
          :thead-class="theadClass"
          :header-classes="headerClasses"
          :cell-classes="cellClasses"
        />
      </div>
    </div>
  </div>
</template>

<script>
import SkeletonCard from '../../../../components/SkeletonCard.vue';
import GeneralCard from '../../../../components/GeneralCard.vue';
import GenericTable from '../../../../components/GenericTable.vue';
import GenericBarChart from '../../../../components/GenericBarChart.vue';
import GenericPieChart from '../../../../components/GenericPieChart.vue';
import services from '../../../../services';
import jwt from 'jsonwebtoken';

const API_ENDPOINTS = {
  SUMMARY_CARDS: 'summary-cards',
  DETAILS_PROPOSALS_STATUS: 'details-proposals-status',
  INITIAL_CHART_DATA: 'initial-chart-data',
};

export default {
  name: 'PainelGeral',
  props: {
    isPresentationMode: Boolean,
    initialDate: String,
    finalDate: String,
  },
  components: {
    SkeletonCard,
    GeneralCard,
    GenericTable,
    GenericBarChart,
    GenericPieChart,
  },
  data() {
    return {
      isLoading: false,
      registerCardsData: {},
      registerDetailsStatus: [],
      tableHeaders: [
        'Top',
        'Nome',
        'Atribuições',
        'Aprovações',
        'Aguardando Aprovação',
        'Pendências',
        'Reprovações',
      ],
      approvedProposalsByWeek: [],
      conversionRateData: 0,
      currentCompany: null,
    };
  },
  computed: {
    tableStyle() {
      const styles = {
        maxHeight: '100%',
        overflowY: 'auto',
      };

      if (this.isPresentationMode) {
        styles.maxHeight = 'none';
        styles.overflowY = 'hidden';
      }

      return styles;
    },
    theadStyle() {
      return {
        position: this.isPresentationMode ? 'relative' : 'sticky',
        top: this.isPresentationMode ? '' : '0',
        zIndex: this.isPresentationMode ? 'auto' : '1',
        backgroundColor: this.isPresentationMode ? 'transparent' : '#f3f4f6',
        width: '100%',
      };
    },
    theadClass() {
      return { 'shadow-custom': !this.isPresentationMode };
    },
    headerClasses() {
      return 'p-4 text-xs sm:text-sm md:text-base text-center bg-gray-300 rounded h-8';
    },
    cellClasses() {
      return 'px-6 py-4 text-xs sm:text-sm md:text-base text-center bg-gray-200 h-8 rounded';
    },
  },
  methods: {
    async fetchData(endpoint) {
      try {
        const { data } = await services.post(
          'super-ranking-register',
          endpoint,
          {
            startDate: this.initialDate,
            endDate: this.finalDate,
            companyId: this.currentCompany,
          },
        );
        return data;
      } catch (error) {
        console.error(`Error fetching data from ${endpoint}:`, error);
      }
    },
    async loadData() {
      this.isLoading = true;
      try {
        const [summaryData, detailsData, initialChartData] = await Promise.all([
          this.fetchData(API_ENDPOINTS.SUMMARY_CARDS),
          this.fetchData(API_ENDPOINTS.DETAILS_PROPOSALS_STATUS),
          this.fetchData(API_ENDPOINTS.INITIAL_CHART_DATA),
        ]);
        this.registerCardsData = summaryData;
        this.registerDetailsStatus = detailsData;
        this.approvedProposalsByWeek = initialChartData.approvedProposalsByWeek;
        this.conversionRateData = initialChartData.conversionRateData;
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        this.isLoading = false;
      }
    },
    getDecoded() {
      const token = localStorage.getItem('user-token');
      if (token) {
        const decoded = jwt.decode(token);
        this.currentCompany = decoded.id_empresa;
      }
    },
  },
  async mounted() {
    this.getDecoded();
    await this.loadData();
  },
};
</script>

<style scoped></style>
